import React, { useEffect, useMemo, useState } from "react";
import GroupedBarChart from "../../../CHARTS/GroupedBarChart";
import { flattenData, flattenDeep } from "../../../Utilitie";

const HeadOverView = ({
  data,
  setSelectedHead,
  from,
  selectedHead,
  subCauseWiseChart,
  height,
}) => {
  flattenData(data);
  const [chartDataSidingWise, setChartDataSidingWise] = useState(null);
  const [chartDataSidingWiseYearly, setChartDataSidingWiseYearly] =
    useState(null);

  const transformYearlyData = useMemo(
    () => (data) => {
      const allYearKeys = new Set();
      const allShifts = new Set();
      const groupMap = {};
      let greatestYear = null;
      let previousYear = null;

      // Step 1: Identify all years and find the greatest and previous year
      data.forEach((yearEntry) => {
        const year = yearEntry.F_YEAR;
        allYearKeys.add(year);
        if (!greatestYear || year > greatestYear) {
          previousYear = greatestYear;
          greatestYear = year;
        } else if (
          !previousYear ||
          (year > previousYear && year < greatestYear)
        ) {
          previousYear = year;
        }
      });

      // Step 2: Process data based on 'from' value
      if (from === "asset") {
        data.forEach((yearEntry) => {
          const year = yearEntry.F_YEAR;
          yearEntry.children.forEach((asset_group) => {
            // Initialize groupMap entry if it doesn't exist
            if (!groupMap[asset_group.ASSET_GROUP]) {
              groupMap[asset_group.ASSET_GROUP] = {
                product: asset_group.ASSET_GROUP,
              };
            }

            let yearAssetTotal = parseFloat(
              groupMap[asset_group.ASSET_GROUP][year] || 0,
            );

            asset_group.children.forEach((main_cause) => {
              main_cause.children.forEach((sub_cause) => {
                sub_cause.children.forEach((month) => {
                  // Add the number of data entries for this month
                  yearAssetTotal += month.data.length;
                });
              });
            });

            groupMap[asset_group.ASSET_GROUP][year] = yearAssetTotal;
          });
        });
      } else if (from === "Sub Cause") {
        data.forEach((yearEntry) => {
          const year = yearEntry.F_YEAR;
          if (year === greatestYear || year === previousYear) {
            yearEntry.children.forEach((asset_group) => {
              asset_group.children.forEach((main_cause) => {
                main_cause.children.forEach((sub_cause) => {
                  if (!groupMap[sub_cause.SUB_CAUSE_DESC]) {
                    groupMap[sub_cause.SUB_CAUSE_DESC] = {
                      product: sub_cause.SUB_CAUSE_DESC,
                    };
                  }

                  sub_cause.children.forEach((month) => {
                    month.data.forEach((entry) => {
                      const shift = entry.SHIFT;
                      allShifts.add(shift); // Collect unique shifts

                      const yearLabel =
                        year === greatestYear
                          ? greatestYear
                          : `${previousYear}`;
                      const key = `${shift} in ${yearLabel}`;
                      let shiftAssetTotal = parseFloat(
                        groupMap[sub_cause.SUB_CAUSE_DESC][key] || 0,
                      );
                      shiftAssetTotal += 1; // Increment by 1 for each data entry
                      groupMap[sub_cause.SUB_CAUSE_DESC][key] = shiftAssetTotal;
                    });
                  });
                });
              });
            });
          }
        });
      } else {
        data.forEach((yearEntry) => {
          const year = yearEntry.F_YEAR;
          yearEntry.children.forEach((asset_group) => {
            asset_group.children.forEach((main_cause) => {
              main_cause.children.forEach((sub_cause) => {
                sub_cause.children.forEach((month) => {
                  month.data.forEach((dataSet) => {
                    // Initialize groupMap entry if it doesn't exist
                    if (!groupMap[dataSet.SECTION_NAME]) {
                      groupMap[dataSet.SECTION_NAME] = {
                        product: dataSet.SECTION_NAME,
                      };
                    }

                    let yearSectionTotal = parseFloat(
                      groupMap[dataSet.SECTION_NAME][year] || 0,
                    );
                    yearSectionTotal += 1; // Increment by 1 for each data entry
                    groupMap[dataSet.SECTION_NAME][year] = yearSectionTotal;
                  });
                });
              });
            });
          });
        });
      }

      // Step 3: Create dimensions and series
      let dimensions;
      let series = Object.values(groupMap);

      if (from === "Sub Cause") {
        dimensions = [
          "product",
          ...Array.from(allShifts)
            .sort()
            .flatMap((shift) => [
              `${shift} in ${previousYear}`,
              `${shift} in ${greatestYear}`,
            ]),
        ];

        series.sort((a, b) => {
          const shifts = ["08_16", "16_24", "00_08"];
          const maxA = Math.max(
            ...shifts.map((shift) => a[`${shift} in ${greatestYear}`] || 0),
          );
          const maxB = Math.max(
            ...shifts.map((shift) => b[`${shift} in ${greatestYear}`] || 0),
          );
          return maxB - maxA; // Descending order
        });
      } else {
        dimensions = ["product", ...Array.from(allYearKeys).sort()];
        series.sort((a, b) => {
          const valueA = parseFloat(a[greatestYear] || 0);
          const valueB = parseFloat(b[greatestYear] || 0);
          return valueB - valueA;
        });
      }

      // Step 4: Return dimensions and sorted series
      return { dimensions, series };
    },
    [],
  );
  const transformYearlyDataYearly = useMemo(
    () => (data) => {
      if (!data || !Array.isArray(data) || data.length === 0) {
        return { dimensions: [], series: [] };
      }

      // Step 1: Find the two most recent years
      const yearKeys = [...new Set(data.map((y) => y.F_YEAR))].sort();
      const greatestYear = yearKeys[yearKeys.length - 1];
      const previousYear = yearKeys[yearKeys.length - 2] || greatestYear;

      const groupMap = {}; // Will hold final data per category

      // Step 2: Process data based on 'from' parameter
      data.forEach((yearEntry) => {
        const year = yearEntry.F_YEAR;

        yearEntry.children.forEach((assetGroup) => {
          assetGroup.children.forEach((mainCause) => {
            mainCause.children.forEach((subCause) => {
              subCause.children.forEach((month) => {
                month.data.forEach((entry) => {
                  let categoryKey;
                  let valueKey;

                  // Determine category and year key based on 'from'
                  if (from === "asset") {
                    categoryKey = assetGroup.ASSET_GROUP;
                    valueKey = year;
                  } else if (from === "Sub Cause") {
                    categoryKey = subCause.SUB_CAUSE_DESC;
                    // Only count last two years
                    if (year !== greatestYear && year !== previousYear) return;
                    valueKey =
                      year === greatestYear ? greatestYear : previousYear;
                  } else {
                    // default: section
                    categoryKey =
                      from === "Coa Section"
                        ? entry.COA_SECTION
                        : entry.SECTION_NAME;
                    valueKey = year;
                  }

                  // Initialize group if not exists
                  if (!groupMap[categoryKey]) {
                    groupMap[categoryKey] = { product: categoryKey };
                  }

                  // Initialize year column
                  if (!groupMap[categoryKey][valueKey]) {
                    groupMap[categoryKey][valueKey] = 0;
                  }

                  // Count occurrence (sum all shifts)
                  groupMap[categoryKey][valueKey] += 1;
                });
              });
            });
          });
        });
      });

      // Step 3: Build dimensions (x-axis labels)
      let dimensions;
      if (from === "Sub Cause") {
        dimensions = ["product", previousYear, greatestYear];
      } else {
        dimensions = ["product", ...yearKeys];
      }

      // Step 4: Convert to series array and sort
      let series = Object.values(groupMap);
      if (from === "Sub Cause") {
        // Sort by latest year (greatestYear) descending
        series.sort((a, b) => (b[greatestYear] || 0) - (a[greatestYear] || 0));
      } else {
        // Sort by most recent year (last in yearKeys)
        series.sort((a, b) => (b[greatestYear] || 0) - (a[greatestYear] || 0));
      }

      return { dimensions, series };
    },
    [from], // Important: re-run when 'from' changes
  );
  useEffect(() => {
    if (data && data.length > 0) {
      const calChartDataSidingWise = transformYearlyData(data);

      setChartDataSidingWise(calChartDataSidingWise);
    } else {
      setChartDataSidingWise(null);
    }
  }, [data, transformYearlyData]);
  useEffect(() => {
    if (data && data.length > 0) {
      const temp = transformYearlyDataYearly(data);

      setChartDataSidingWiseYearly(temp);
    } else {
      setChartDataSidingWiseYearly(null);
    }
  }, [data]);
  return (
    <div className="papaDiv" style={{ background: "rgba(0,0,0,0)" }}>
      {from === "Sub Cause" || (
        <h6 className="bg-dark text-white p-1">
          {from === "asset" ? "Head" : from} wise cause
        </h6>
      )}
      <hr className="m-1 p-0" />
      {subCauseWiseChart && chartDataSidingWise && (
        <GroupedBarChart
          selectedHead={selectedHead}
          setSelectedHead={setSelectedHead}
          data={chartDataSidingWise}
          from="AssetFailure"
          subFrom={from}
          height={height}
          rotateText={10}
          chartType="bar"
        />
      )}
      {!subCauseWiseChart && chartDataSidingWise && (
        <GroupedBarChart
          selectedHead={selectedHead}
          setSelectedHead={setSelectedHead}
          data={chartDataSidingWiseYearly}
          from="AssetFailure"
          subFrom={from}
          height={height}
          chartType="bar"
          rotateText={10}
        />
      )}
    </div>
  );
};

export default HeadOverView;
